import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './components/layout/Navbar.jsx';
import Footer from './components/layout/Footer.jsx';
import RequireAuth from './components/layout/RequireAuth.jsx';
import RequireAdmin from './components/layout/RequireAdmin.jsx';
import { useFeatureFlags } from './hooks/useFeatureFlags.js';
import { useLanguage, LANGUAGES } from './hooks/useLanguage.js';
import { useAuthStore } from './store/authStore.js';
import api from './api/client.js';

// --- Route-based code splitting ---------------------------------------------
// Every page (and every individual admin tab) is its own dynamic import, so
// Vite emits a separate chunk per route instead of bundling the whole app —
// including react-pageflip (pulled in by Flipbook.jsx) and the entire Admin
// Panel — into the single main.js a regular reader downloads on first paint.
// A logged-out reader on "/" now only fetches the Home chunk (+ its own
// dependencies); nothing under client/src/admin is requested until an admin
// actually navigates to /admin/*.
const Home = lazy(() => import('./pages/Home.jsx'));
const FlipbookPage = lazy(() => import('./pages/FlipbookPage.jsx'));
// Monthly Magazine (Phase 1 — Foundation): a separate, admin-curated
// publication with zero dependency on the news-article flipbook above — see
// server/src/models/MagazineIssue.js's header comment.
const MagazineViewer = lazy(() => import('./pages/MagazineViewer.jsx'));
const MagazineArchive = lazy(() => import('./pages/MagazineArchive.jsx'));
// Print Magazine order form (Phase 2 — Commerce): a normal chrome'd page,
// NOT full-bleed like the flipbook viewer above — see isFullBleed below.
const PrintMagazineOrder = lazy(() => import('./pages/PrintMagazineOrder.jsx'));
// Print-Ad Marketplace (Phase 3): booking ad space inside a print issue —
// a normal chrome'd page, entirely separate from the online ad marketplace
// below (Marketplace.jsx) and from PrintMagazineOrder.jsx.
const PrintAdOrder = lazy(() => import('./pages/PrintAdOrder.jsx'));
const Marketplace = lazy(() => import('./pages/Marketplace.jsx'));
const MockCheckout = lazy(() => import('./pages/MockCheckout.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
// Customer Support Tickets (Phase 4): a normal chrome'd, RequireAuth-wrapped
// page — the ticket list/creation form lives on Dashboard.jsx, this page is
// just the full thread for one ticket.
const SupportTicket = lazy(() => import('./pages/SupportTicket.jsx'));
const Login = lazy(() => import('./pages/auth/Login.jsx'));
const Signup = lazy(() => import('./pages/auth/Signup.jsx'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail.jsx'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword.jsx'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword.jsx'));
const DmcaForm = lazy(() => import('./pages/DmcaForm.jsx'));
const AboutUs = lazy(() => import('./pages/AboutUs.jsx'));

// Admin layout + each of its nested tabs are split individually (not just as
// one combined "admin" chunk) so opening e.g. Analytics doesn't also pull
// down Sources/Review Queue/Ad Products/Media/Magazine/DMCA Queue code.
const AdminLayout = lazy(() => import('./admin/AdminLayout.jsx'));
const AdminSources = lazy(() => import('./admin/AdminSources.jsx'));
const AdminArticles = lazy(() => import('./admin/AdminArticles.jsx'));
const AdminCategories = lazy(() => import('./admin/AdminCategories.jsx'));
const AdminLiveActivity = lazy(() => import('./admin/AdminLiveActivity.jsx'));
const AdminPodcast = lazy(() => import('./admin/AdminPodcast.jsx'));
const AdminFeaturedArticles = lazy(() => import('./admin/AdminFeaturedArticles.jsx'));
const AdminUsers = lazy(() => import('./admin/AdminUsers.jsx'));
const AdminFeatureFlags = lazy(() => import('./admin/AdminFeatureFlags.jsx'));
const AdminComplimentaryAds = lazy(() => import('./admin/AdminComplimentaryAds.jsx'));
const AdminReviewQueue = lazy(() => import('./admin/AdminReviewQueue.jsx'));
const AdminAdProducts = lazy(() => import('./admin/AdminAdProducts.jsx'));
const AdminAnalytics = lazy(() => import('./admin/AdminAnalytics.jsx'));
const AdminDmca = lazy(() => import('./admin/AdminDmca.jsx'));
const AdminAdsManagement = lazy(() => import('./admin/AdminAdsManagement.jsx'));
const AdminMagazine = lazy(() => import('./admin/AdminMagazine.jsx'));
const AdminPrintAds = lazy(() => import('./admin/AdminPrintAds.jsx'));
// Phase 4: Magazine Commerce Analytics + Customer Support Tickets — each its
// own chunk, same rationale as every other admin tab above.
const AdminCommerceAnalytics = lazy(() => import('./admin/AdminCommerceAnalytics.jsx'));
const AdminSupport = lazy(() => import('./admin/AdminSupport.jsx'));
// Phase 5: Discount Coupons (spec Sections 5 & 8.4).
const AdminCoupons = lazy(() => import('./admin/AdminCoupons.jsx'));

/**
 * Feature Flags / Visibility Toggles: client-side route gate for the four
 * monetization pages — renders NotFound instead of the real page when its
 * flag is off, so a bookmarked/guessed URL doesn't render a dead page full
 * of 404ing API calls. The actual security boundary is server-side (see
 * middleware/featureFlag.js) — this is purely so the client experience
 * matches "completely invisible and inaccessible" rather than showing a
 * broken page.
 */
function FeatureGate({ flag, children }) {
  const featureFlags = useFeatureFlags();
  return featureFlags[flag] ? children : <NotFound />;
}

function RouteLoadingFallback() {
  // Kept intentionally tiny/dependency-free — it ships inside App.jsx's own
  // (eager) chunk, so it's available instantly, before any lazy chunk has
  // had a chance to load.
  return (
    <div style={{ padding: 80, textAlign: 'center', color: 'var(--muted)' }} role="status" aria-live="polite">
      Loading…
    </div>
  );
}

export default function App() {
  // The standalone Flipbook route (per an explicit "pure clean webpage"
  // design request) hides the site Navbar/Footer entirely — nothing on
  // screen except the magazine and its own Return button, which navigates
  // back to "/" where the normal chrome returns. The Monthly Magazine
  // viewer (both "/magazine" — current issue — and "/magazine/:id" — an
  // archived issue) gets the same full-bleed treatment.
  //
  // This is deliberately NOT a blanket "startsWith('/magazine')" check
  // (that was Phase 1's logic, before "/magazine/archive" was the only
  // other magazine route). Phase 2 — Commerce added "/magazine/order", a
  // normal chrome'd page that must keep the Navbar/Footer, so the full-bleed
  // check is now an explicit allowlist: exactly "/magazine", or
  // "/magazine/<24-hex-char Mongo id>" (an archived issue). Every other
  // "/magazine/*" route — "/magazine/archive", "/magazine/order", and any
  // future addition — falls through to the normal chrome by default, which
  // is the safer failure mode here.
  const location = useLocation();
  const isMagazineIssueId = /^\/magazine\/[a-f0-9]{24}$/i.test(location.pathname);
  // The Monthly Magazine's full-bleed (no Navbar/Footer) treatment only applies
  // while the magazine is actually visible — with Settings.featureFlags.
  // magazineEnabled off, /magazine and /magazine/<id> render FeatureGate's
  // NotFound, which must keep the normal site chrome so it isn't a dead end.
  const featureFlags = useFeatureFlags();
  const isFullBleed =
    location.pathname === '/flipbook' ||
    (featureFlags.magazineEnabled && (location.pathname === '/magazine' || isMagazineIssueId));

  // Session bootstrap — runs exactly once, on first mount. The auth session
  // lives entirely in an httpOnly cookie (see store/authStore.js's header
  // comment), which this app can never read directly, so on every fresh
  // page load/refresh/new tab the client genuinely doesn't know yet whether
  // the visitor is logged in. This is the one place that finds out: ask the
  // server, then populate (or clear) the store with the answer. Until this
  // resolves, useAuthStore's status stays 'loading' — see RequireAuth.jsx/
  // RequireAdmin.jsx, which both wait for it rather than assuming
  // "logged out" and redirecting a perfectly valid session to /login.
  useEffect(() => {
    api
      .get('/auth/me')
      .then((res) => useAuthStore.getState().setUser(res.data.user))
      .catch(() => useAuthStore.getState().setUser(null));
  }, []);

  // Frontend Language Switcher & Cookie Persistence: dir="rtl" for
  // Persian/Arabic (and back to "ltr" for everything else) is applied at
  // the document root so the WHOLE page — nav, article grid, footer —
  // flows right-to-left together, not just individual translated text
  // fragments. lang is kept in sync too for accessibility/screen readers.
  const { language, isRtl, setLanguage } = useLanguage();
  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRtl]);

  // Deep-linkable language selection (?lang=xx) — lets a shareable/bookmarked
  // URL (e.g. "/?lang=es") open the site already switched to a specific
  // language, rather than the choice only being reachable through the
  // LanguageSwitcher dropdown in the navbar. Applying it calls the exact
  // same setLanguage() the dropdown uses, so it persists to localStorage
  // (store/languageStore.js) and stays selected for the rest of this visit
  // and on future visits too — not just for this one page load.
  //
  // Read once, straight off window.location.search rather than react-
  // router's location — an empty dependency array means this only runs on
  // the initial page load. A subsequent in-app navigation (e.g. clicking a
  // navbar link) is a client-side route change, not a fresh load, and
  // normally drops the query string entirely, so there is nothing to
  // reprocess (and nothing here would fight a manual dropdown switch mid-
  // visit, since this effect never runs again after mount). Unknown/
  // malformed values are ignored silently rather than surfacing an error —
  // this is a convenience deep link, not a validated input field.
  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get('lang');
    if (requested && requested !== language && LANGUAGES.some((l) => l.code === requested)) {
      setLanguage(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isFullBleed && <Navbar />}
      <div style={{ flex: 1 }}>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/flipbook" element={<FlipbookPage />} />
            {/* The internal /article/:slug detail page has been removed —
                "Read more" links now go straight to each article's
                canonicalUrl. See ArticleList.jsx / FlipbookPage.jsx. */}

            {/* Monthly Magazine — the archive listing and order form must be
                declared before the ":id" route below; React Router v6 ranks
                static path segments above dynamic ones by specificity
                regardless of declaration order, but the explicit ordering
                here still documents the intent for anyone reading this list
                top to bottom. */}
            <Route
              path="/magazine"
              element={
                <FeatureGate flag="magazineEnabled">
                  <MagazineViewer />
                </FeatureGate>
              }
            />
            <Route
              path="/magazine/archive"
              element={
                <FeatureGate flag="magazineEnabled">
                  <FeatureGate flag="printMagazineArchiveEnabled">
                    <MagazineArchive />
                  </FeatureGate>
                </FeatureGate>
              }
            />
            {/* Phase 2 — Commerce: print purchasing/subscription form. Auth
                is enforced inside the page itself (redirects to /login on
                submit if logged out) rather than via RequireAuth, so logged
                -out visitors can still browse pricing and build a quote. */}
            <Route
              path="/magazine/order"
              element={
                <FeatureGate flag="printMagazineStoreEnabled">
                  <PrintMagazineOrder />
                </FeatureGate>
              }
            />
            <Route
              path="/magazine/:id"
              element={
                <FeatureGate flag="magazineEnabled">
                  <MagazineViewer />
                </FeatureGate>
              }
            />

            <Route
              path="/marketplace"
              element={
                <FeatureGate flag="onlineAdBookingEnabled">
                  <Marketplace />
                </FeatureGate>
              }
            />
            {/* Print-Ad Marketplace (Phase 3): same "enforce auth inside the
                page, not via RequireAuth" pattern as /magazine/order above —
                a logged-out visitor can still see pricing and build a quote. */}
            <Route
              path="/print-ads/order"
              element={
                <FeatureGate flag="printAdBookingEnabled">
                  <PrintAdOrder />
                </FeatureGate>
              }
            />
            <Route path="/mock-checkout" element={<MockCheckout />} />
            <Route path="/dmca" element={<DmcaForm />} />
            <Route path="/about" element={<AboutUs />} />

            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            {/* Customer Support Tickets (Phase 4): a single ticket's full
                thread — the list/creation form lives on Dashboard above. */}
            <Route
              path="/support/:id"
              element={
                <RequireAuth>
                  <SupportTicket />
                </RequireAuth>
              }
            />

            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<Navigate to="analytics" replace />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="sources" element={<AdminSources />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="live-activity" element={<AdminLiveActivity />} />
              <Route path="podcast" element={<AdminPodcast />} />
              <Route path="featured-articles" element={<AdminFeaturedArticles />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="feature-flags" element={<AdminFeatureFlags />} />
              <Route path="complimentary-ads" element={<AdminComplimentaryAds />} />
              <Route path="review-queue" element={<AdminReviewQueue />} />
              <Route path="ads" element={<AdminAdsManagement />} />
              <Route path="ad-products" element={<AdminAdProducts />} />
              <Route path="magazine" element={<AdminMagazine />} />
              <Route path="print-ads" element={<AdminPrintAds />} />
              <Route path="commerce-analytics" element={<AdminCommerceAnalytics />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="dmca" element={<AdminDmca />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {!isFullBleed && <Footer />}
    </div>
  );
}

function NotFound() {
  return (
    <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
      <Helmet>
        <title>Page Not Found — The USA Rules Magazine</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <h1>404 — Page not found</h1>
      <p>The page you're looking for doesn't exist.</p>
    </div>
  );
}

